import React, { memo, useCallback, useEffect, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { PageHeader } from 'src/components/payments-ops/PageHeader'
import { ToolbarCheckbox } from 'src/components/payments-ops/ToolbarCheckbox'
import { ToolbarRow } from 'src/components/payments-ops/ToolbarRow'
import { WalletsTable } from 'src/components/payments-ops/wallets/WalletsTable'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { AdminFetchWallets } from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { getWalletsError } from 'src/selectors/wallet'
import { useQueryParamUpdater } from 'src/utils/hooks/paymentsOps/useQueryParamUpdater'

const WalletsPageComponent = () => {
  const [searchParams] = useSearchParams()
  const updateParams = useQueryParamUpdater()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const listError = useSelector(getWalletsError)

  const [refreshToken, setRefreshToken] = useState<number>(0)

  // Default matches the backend: wallets of deleted partners are INCLUDED.
  const activeOnly = searchParams.get('activeOnly') === 'true'

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchWallets.request({ activeOnly }))
  }, [activeOnly, asyncDispatchWithErrorToast, isAuthenticated, refreshToken])

  const handleActiveOnlyChange = useCallback(
    (nextActiveOnly: boolean) => {
      // Never rewrite the URL when nothing changed.
      if (nextActiveOnly === activeOnly) {
        return
      }

      updateParams({ activeOnly: nextActiveOnly ? 'true' : null })
    },
    [activeOnly, updateParams],
  )

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  return (
    <div
      data-testid="wallets-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
    >
      <PageHeader
        title="Wallets"
        description="Virtual-account balances. Available = total minus amounts locked by in-flight payments and uncollected fees."
      />
      <ToolbarRow>
        <ToolbarCheckbox
          name="walletsActiveOnly"
          label="Active partners only"
          checked={activeOnly}
          onChange={handleActiveOnlyChange}
        />
        <div css={tw`ml-auto pb-1`}>
          <Button
            size="small"
            color="white"
            leftIcon={<GSIcon iconName="gs_arrows_repeat" size={20} css={tw`text-shades-1000`} />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </div>
      </ToolbarRow>
      {listError ? (
        <Typography variant="body2" css={tw`bg-shades-0 px-4 py-3 text-critical-500`}>
          {listError}
        </Typography>
      ) : null}
      <WalletsTable />
    </div>
  )
}

export const WalletsPage = memo(WalletsPageComponent)
